import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Header } from '../components/layout/Header';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useAdminTranslation } from '../utils/useAdminTranslation';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { 
  ArrowLeft, 
  ExternalLink, 
  AlertTriangle, 
  CheckCircle, 
  Upload,
  Calendar,
  Loader2,
  Wallet,
  CreditCard,
  AlertCircle,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const CheckoutPage = () => {
  const { cartItems, checkout, getCartTotal, clearCart, appliedCoupon, setAppliedCoupon } = useCart();
  const { token } = useAuth();
  const { language, tx } = useAdminTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderData, setOrderData] = useState(null);
  const [walletBalance, setWalletBalance] = useState(0);
  const [walletLoading, setWalletLoading] = useState(true);
  const [insufficientModal, setInsufficientModal] = useState(null);  // {shortfall, total, current, amount}
  const [customAmount, setCustomAmount] = useState('');  // amount the user wants to top up (min 10€)
  const [payingPaypal, setPayingPaypal] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  
  // Fetch wallet balance
  useEffect(() => {
    const fetchWallet = async () => {
      try {
        const response = await fetch(`${API_URL}/api/wallet`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setWalletBalance(data.balance || 0);
        }
      } catch (error) {
        console.error('Error fetching wallet:', error);
      } finally {
        setWalletLoading(false);
      }
    };
    fetchWallet();
  }, [token]);
  
  // Form data for each cart item
  const [formData, setFormData] = useState(() => {
    const initial = {};
    cartItems.forEach(item => {
      initial[item.id] = {
        price_type: item.price_type || 'normal',
        link_type: '',
        anchor_1: '',
        url_1: '',
        anchor_2: '',
        url_2: '',
        word_count: '',
        publication_date: '',
        review_before_publish: false,
        // Internal link fields
        add_internal_link: false,
        internal_link_url: '',
        internal_link_anchor: '',
        // Authority site link fields
        add_authority_link: false,
        authority_link_url: '',
        authority_link_anchor: '',
        description: '',
        article_file: null,
        image_file_1: null,
        image_file_2: null,
      };
    });
    return initial;
  });

  const updateFormData = (itemId, field, value) => {
    setFormData(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        [field]: value,
      }
    }));
  };

  const handleFileChange = (itemId, field, file) => {
    setFormData(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        [field]: file,
      }
    }));
  };

  const getWarnings = (item) => {
    const warnings = [];
    const rawLinkType = (item.link_type || '').toLowerCase();
    
    // Parse the link_type to check what types are accepted
    const parts = rawLinkType.split(',').map(p => p.trim());
    let hasDoFollow = parts.some(p => p === 'follow' || p === 'dofollow' || p === 'do-follow');
    let hasNoFollow = parts.some(p => p === 'no-follow' || p === 'nofollow');
    let hasSponsored = parts.some(p => p === 'sponsored');
    
    // Only show warning if product DOES NOT accept DoFollow
    if (!hasDoFollow && rawLinkType) {
      if (hasNoFollow && !hasSponsored) {
        warnings.push({ type: 'warning', text: '⚠️ Solo NoFollow', priority: 1 });
      } else if (hasSponsored && !hasNoFollow) {
        warnings.push({ type: 'warning', text: '⚠️ Solo Sponsored', priority: 1 });
      } else if (hasNoFollow && hasSponsored) {
        warnings.push({ type: 'warning', text: '⚠️ NoFollow/Sponsored (sin DoFollow)', priority: 1 });
      }
    }
    
    // Ad label warning - IMPORTANT if has ad label
    if (item.ad_label === true || item.ad_label === 'Sí' || item.ad_label === 'Yes' || item.ad_label === 'SI' || item.ad_label === 'Si') {
      warnings.push({ type: 'error', text: tx('⚠️ Lleva Etiqueta Publicitaria', '⚠️ Has Ad Label'), priority: 0 });
    }
    
    // Link duration - Warning if NOT permanent
    const duration = (item.link_duration || item.duration || '').toLowerCase();
    if (duration && duration !== 'permanente' && duration !== 'permanent') {
      warnings.push({ type: 'warning', text: tx(`⚠️ Duración: ${item.link_duration || item.duration}`, `⚠️ Duration: ${item.link_duration || item.duration}`), priority: 1 });
    }
    
    return warnings.sort((a, b) => a.priority - b.priority);
  };

  // Get available link types for a product
  const getAvailableLinkTypes = (item) => {
    const rawLinkType = (item.link_type || '').toLowerCase();
    const availableTypes = [];
    
    // Parse the link_type string - examples: "follow", "follow, no-follow", "no-follow", "sponsored"
    // "follow" alone means DoFollow
    // "no-follow" or "nofollow" means NoFollow
    
    // Check for each type by looking at the raw string
    // "follow" without "no-follow" before it = DoFollow
    const parts = rawLinkType.split(',').map(p => p.trim());
    
    let hasDoFollow = false;
    let hasNoFollow = false;
    let hasSponsored = false;
    let hasUGC = false;
    
    for (const part of parts) {
      if (part === 'follow' || part === 'dofollow' || part === 'do-follow') {
        hasDoFollow = true;
      }
      if (part === 'no-follow' || part === 'nofollow') {
        hasNoFollow = true;
      }
      if (part === 'sponsored') {
        hasSponsored = true;
      }
      if (part === 'ugc') {
        hasUGC = true;
      }
    }
    
    // If product accepts DoFollow, user can choose any type
    if (hasDoFollow) {
      availableTypes.push({ value: 'dofollow', label: 'DoFollow' });
      availableTypes.push({ value: 'nofollow', label: 'NoFollow' });
      availableTypes.push({ value: 'sponsored', label: 'Sponsored' });
      availableTypes.push({ value: 'ugc', label: 'UGC' });
    }
    // If product only accepts NoFollow (not DoFollow)
    else if (hasNoFollow) {
      availableTypes.push({ value: 'nofollow', label: 'NoFollow' });
      availableTypes.push({ value: 'sponsored', label: 'Sponsored' });
      availableTypes.push({ value: 'ugc', label: 'UGC' });
    }
    // If product only accepts Sponsored
    else if (hasSponsored) {
      availableTypes.push({ value: 'sponsored', label: 'Sponsored' });
    }
    // If product only accepts UGC
    else if (hasUGC) {
      availableTypes.push({ value: 'ugc', label: 'UGC' });
    }
    // Default - show all options if no specific type or empty
    else {
      availableTypes.push({ value: 'dofollow', label: 'DoFollow' });
      availableTypes.push({ value: 'nofollow', label: 'NoFollow' });
      availableTypes.push({ value: 'sponsored', label: 'Sponsored' });
      availableTypes.push({ value: 'ugc', label: 'UGC' });
    }
    
    return availableTypes;
  };

  // Calculate writing cost based on word count
  const calculateWritingCost = (wordCount) => {
    if (!wordCount || wordCount === 'own_text') return 0; // Cliente envía el texto = gratis
    const words = parseInt(wordCount);
    if (words <= 500) return 0; // Hasta 500 palabras = gratis
    if (words === 600) return 6;
    if (words === 700) return 7;
    if (words === 800) return 8;
    if (words === 900) return 9;
    if (words >= 1000) return 10;
    return 0;
  };

  const calculateItemPrice = (item) => {
    const priceType = formData[item.id]?.price_type || 'normal';
    let basePrice = 0;
    if (priceType === 'sensitive') {
      basePrice = item.sensitive_price || item.price_sensitive || item.price || 0;
    } else {
      basePrice = item.price || 0;
    }
    
    // Add writing cost
    const wordCount = formData[item.id]?.word_count;
    const writingCost = calculateWritingCost(wordCount);
    
    return basePrice + writingCost;
  };

  const calculateTotal = () => {
    return cartItems.reduce((total, item) => total + calculateItemPrice(item), 0);
  };

  // Pagar con PayPal el importe que el usuario quiera recargar (mínimo 10€).
  // El monedero se acredita y, si cubre el total, el pedido se finaliza automáticamente.
  const handlePayShortfall = async () => {
    if (!insufficientModal || payingPaypal) return;
    const parsed = parseFloat(String(customAmount).replace(',', '.'));
    if (isNaN(parsed) || parsed < 10) {
      toast({
        title: tx('Importe no válido', 'Invalid amount'),
        description: tx('El importe mínimo de recarga es 10€.', 'Minimum top-up amount is €10.'),
        variant: 'destructive',
      });
      return;
    }
    const amountNum = Math.round(parsed * 100) / 100;
    setPayingPaypal(true);
    try {
      const createRes = await fetch(`${API_URL}/api/paypal/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ amount: amountNum, purpose: 'wallet_recharge' }),
      });
      if (!createRes.ok) {
        const err = await createRes.json().catch(() => ({}));
        throw new Error(err.detail || 'No se pudo crear la orden PayPal');
      }
      const { order_id, approve_url } = await createRes.json();

      const w = 520, h = 700;
      const left = (window.screen.width - w) / 2;
      const top = (window.screen.height - h) / 2;
      const popup = window.open(approve_url, 'paypal_popup', `width=${w},height=${h},left=${left},top=${top}`);
      if (!popup) {
        toast({
          title: 'Error',
          description: tx('Desactiva el bloqueador de pop-ups', 'Please disable the pop-up blocker'),
          variant: 'destructive',
        });
        setPayingPaypal(false);
        return;
      }

      let captured = false;
      const finishCapture = async (success) => {
        if (captured) return;
        captured = true;
        clearInterval(interval);
        window.removeEventListener('message', onMessage);
        try { popup.close(); } catch (_) {}
        if (!success) {
          toast({
            title: tx('Pago cancelado', 'Payment cancelled'),
            description: tx('Has cancelado el pago en PayPal.', 'You cancelled the PayPal payment.'),
            variant: 'destructive',
          });
          setPayingPaypal(false);
          return;
        }
        try {
          const capRes = await fetch(`${API_URL}/api/paypal/capture/${order_id}`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
          });
          const capData = await capRes.json();
          if (capRes.ok && capData.status === 'COMPLETED') {
            // Refrescar balance del monedero
            let newBalance = walletBalance + amountNum;
            try {
              const wr = await fetch(`${API_URL}/api/wallet`, { headers: { 'Authorization': `Bearer ${token}` } });
              if (wr.ok) {
                const wd = await wr.json();
                newBalance = wd.balance || newBalance;
                setWalletBalance(newBalance);
              } else {
                setWalletBalance(newBalance);
              }
            } catch (_) {
              setWalletBalance(newBalance);
            }
            const total = calculateTotal();
            if (newBalance + 0.001 >= total) {
              toast({
                title: tx('¡Recarga completada!', 'Top-up complete!'),
                description: tx(
                  `Se han abonado ${amountNum.toFixed(2)}€. Finalizando tu pedido…`,
                  `€${amountNum.toFixed(2)} credited. Completing your order…`
                ),
              });
              setInsufficientModal(null);
              setCustomAmount('');
              setPayingPaypal(false);
              setTimeout(() => { handleSubmit(); }, 600);
            } else {
              toast({
                title: tx('Recarga abonada', 'Top-up credited'),
                description: tx(
                  `Saldo actualizado: ${newBalance.toFixed(2)}€. Aún faltan ${(total - newBalance).toFixed(2)}€ para cubrir el pedido.`,
                  `New balance: €${newBalance.toFixed(2)}. Still €${(total - newBalance).toFixed(2)} short to cover the order.`
                ),
              });
              setPayingPaypal(false);
              // Mantener el modal abierto con los nuevos datos
              setInsufficientModal({
                current: newBalance,
                total,
                shortfall: Math.max(Math.ceil(total - newBalance), 10),
              });
              setCustomAmount(String(Math.max(Math.ceil(total - newBalance), 10)));
            }
          } else {
            toast({
              title: tx('Pago no completado', 'Payment not completed'),
              description: capData.detail || tx('Se canceló el pago en PayPal.', 'Payment was cancelled in PayPal.'),
              variant: 'destructive',
            });
            setPayingPaypal(false);
          }
        } catch (e) {
          toast({ title: 'Error', description: e.message, variant: 'destructive' });
          setPayingPaypal(false);
        }
      };
      const onMessage = (ev) => {
        const data = ev?.data;
        if (data && data.type === 'paypal_return') {
          finishCapture(data.result === 'success');
        }
      };
      window.addEventListener('message', onMessage);
      const interval = setInterval(() => {
        let returnedToOurDomain = null;
        try {
          const href = popup.location.href;
          if (href && href.indexOf('paypal=success') !== -1) returnedToOurDomain = true;
          else if (href && href.indexOf('paypal=cancel') !== -1) returnedToOurDomain = false;
        } catch (_) {}
        if (returnedToOurDomain !== null) {
          finishCapture(returnedToOurDomain);
        } else if (popup.closed) {
          finishCapture(false);
        }
      }, 400);
      setTimeout(() => {
        if (!captured) {
          captured = true;
          clearInterval(interval);
          window.removeEventListener('message', onMessage);
          setPayingPaypal(false);
          toast({
            title: tx('Tiempo agotado', 'Timeout'),
            description: tx('No se detectó la respuesta de PayPal.', 'PayPal response not detected.'),
            variant: 'destructive',
          });
        }
      }, 240000);
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
      setPayingPaypal(false);
    }
  };

  const handleSubmit = async () => {
    // Validate required fields
    for (const item of cartItems) {
      const data = formData[item.id];
      if (!data.link_type) {
        toast({
          title: tx('Campo requerido', 'Required field'),
          description: tx(
            `Por favor selecciona el Tipo de Enlace para ${item.url || item.website}`,
            `Please select Link Type for ${item.url || item.website}`
          ),
          variant: 'destructive',
        });
        return;
      }
      if (!data.word_count) {
        toast({
          title: tx('Campo requerido', 'Required field'),
          description: tx(
            `Por favor selecciona el Número de Palabras para ${item.url || item.website}`,
            `Please select Word Count for ${item.url || item.website}`
          ),
          variant: 'destructive',
        });
        return;
      }
      if (!data.anchor_1 || !data.url_1) {
        toast({
          title: tx('Campos requeridos', 'Required fields'),
          description: tx(
            `Por favor completa Anchor 1 y URL 1 para ${item.url || item.website}`,
            `Please complete Anchor 1 and URL 1 for ${item.url || item.website}`
          ),
          variant: 'destructive',
        });
        return;
      }
    }

    setLoading(true);
    
    try {
      // Prepare order data
      const ordersData = cartItems.map(item => ({
        product_id: item.product_id,
        price_type: formData[item.id]?.price_type || 'normal',
        link_type: formData[item.id]?.link_type || '',
        anchor_1: formData[item.id]?.anchor_1 || '',
        url_1: formData[item.id]?.url_1 || '',
        anchor_2: formData[item.id]?.anchor_2 || '',
        url_2: formData[item.id]?.url_2 || '',
        word_count: formData[item.id]?.word_count || '',
        writing_cost: calculateWritingCost(formData[item.id]?.word_count), // Coste de redacción
        publication_date: formData[item.id]?.publication_date || '',
        review_before_publish: formData[item.id]?.review_before_publish || false,
        // Internal link data
        add_internal_link: formData[item.id]?.add_internal_link || false,
        internal_link_url: formData[item.id]?.internal_link_url || '',
        internal_link_anchor: formData[item.id]?.internal_link_anchor || '',
        // Authority link data
        add_authority_link: formData[item.id]?.add_authority_link || false,
        authority_link_url: formData[item.id]?.authority_link_url || '',
        authority_link_anchor: formData[item.id]?.authority_link_anchor || '',
        description: formData[item.id]?.description || '',
      }));

      console.log('Checkout data:', JSON.stringify({ orders: ordersData, coupon_code: appliedCoupon?.code || null }, null, 2));

      const response = await fetch(`${API_URL}/api/cart/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ orders: ordersData, coupon_code: appliedCoupon?.code || null }),
      });

      if (response.ok) {
        const data = await response.json();
        
        // Clear applied coupon after successful checkout
        setAppliedCoupon(null);
        
        // Upload files for each order
        if (data.order_ids && data.order_ids.length > 0) {
          for (let i = 0; i < cartItems.length; i++) {
            const item = cartItems[i];
            const orderId = data.order_ids[i];
            const itemFormData = formData[item.id];
            
            // Check if there are files to upload
            if (itemFormData?.article_file || itemFormData?.image_file_1 || itemFormData?.image_file_2) {
              const fileFormData = new FormData();
              
              if (itemFormData.article_file) {
                fileFormData.append('article_file', itemFormData.article_file);
              }
              if (itemFormData.image_file_1) {
                fileFormData.append('image_file_1', itemFormData.image_file_1);
              }
              if (itemFormData.image_file_2) {
                fileFormData.append('image_file_2', itemFormData.image_file_2);
              }
              
              try {
                await fetch(`${API_URL}/api/orders/${orderId}/upload-files`, {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${token}`,
                  },
                  body: fileFormData,
                });
                console.log(`Files uploaded for order ${orderId}`);
              } catch (fileError) {
                console.error(`Error uploading files for order ${orderId}:`, fileError);
              }
            }
          }
        }
        
        setOrderSuccess(true);
        setOrderData(data);
        clearCart();
        toast({
          title: tx('¡Pedido realizado!', 'Order placed!'),
          description: tx(
            `Se han creado ${data.orders_created} pedido(s)`,
            `${data.orders_created} order(s) created`
          ),
        });
      } else {
        const errorData = await response.json();
        const detailStr = typeof errorData.detail === 'string' ? errorData.detail : '';
        // Detectar saldo insuficiente → abrir modal bloqueante con PayPal (sin borrar el pedido en memoria)
        if (response.status === 400 && (detailStr.toLowerCase().includes('saldo insuficiente') || detailStr.toLowerCase().includes('insufficient'))) {
          // Extraer saldo actual y total del mensaje del backend:
          // "Saldo insuficiente. Tu saldo es X.XX€ y el total del pedido es Y.YY€..."
          const mCurr = detailStr.match(/([0-9]+(?:[.,][0-9]+)?)\s*€/g) || [];
          const nums = mCurr.map(s => parseFloat(s.replace('€', '').replace(',', '.').trim())).filter(n => !isNaN(n));
          const current = nums.length > 0 ? nums[0] : walletBalance;
          const total = nums.length > 1 ? nums[1] : null;
          const shortfall = total !== null ? Math.max(total - current, 10) : 10;
          const shortfallRounded = Math.ceil(shortfall);
          setInsufficientModal({
            current,
            total,
            shortfall: shortfallRounded,
          });
          setCustomAmount(String(shortfallRounded));
          return;  // No mostrar toast rojo: el modal es la acción
        }
        throw new Error(detailStr || 'Checkout failed');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast({
        title: tx('Error', 'Error'),
        description: error.message || tx('Error al procesar el pedido', 'Error processing order'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  if (cartItems.length === 0 && !orderSuccess) {
    navigate('/cart');
    return null;
  }

  if (orderSuccess) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-12">
          <div className="max-w-lg mx-auto text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold mb-3">
              {tx('¡Pedido realizado con éxito!', 'Order placed successfully!')}
            </h1>
            <p className="text-muted-foreground mb-8">
              {tx(
                'Recibirás un email de confirmación con los detalles de tu pedido.',
                'You will receive a confirmation email with your order details.'
              )}
            </p>
            {orderData && (
              <div className="mb-8 p-4 bg-muted rounded-lg text-sm">
                <p className="font-medium">{tx('Pedidos creados', 'Orders created')}: {orderData.orders_created}</p>
                {orderData.order_numbers && (
                  <p className="text-muted-foreground">Nº: {orderData.order_numbers.join(', ')}</p>
                )}
              </div>
            )}
            <Button onClick={() => navigate('/')} size="lg">
              {tx('Volver al catálogo', 'Back to catalog')}
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate('/cart')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">
              {tx('Finalizar Pedido', 'Complete Order')}
            </h1>
            <p className="text-muted-foreground text-sm">
              {tx(
                'Completa la información para cada producto',
                'Complete the information for each product'
              )}
            </p>
          </div>
        </div>

        {/* Order Forms */}
        <div className="space-y-8">
          {cartItems.map((item, index) => (
            <Card key={item.id} className="overflow-hidden">
              <CardHeader className="bg-muted/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      {item.url || item.website}
                      <a 
                        href={`https://${item.url || item.website}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary hover:text-primary/80"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </CardTitle>
                  </div>
                  <div className="flex gap-2">
                    {getWarnings(item).map((warning, idx) => (
                      <Badge 
                        key={idx} 
                        variant={warning.type === 'error' ? 'destructive' : warning.type === 'warning' ? 'warning' : warning.type === 'success' ? 'success' : 'secondary'}
                        className={
                          warning.type === 'error' ? 'bg-red-100 text-red-700 border-red-200' :
                          warning.type === 'warning' ? 'bg-yellow-100 text-yellow-700 border-yellow-200' :
                          warning.type === 'success' ? 'bg-green-100 text-green-700 border-green-200' :
                          'bg-blue-100 text-blue-700 border-blue-200'
                        }
                      >
                        {warning.text}
                      </Badge>
                    ))}
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="p-6 space-y-6">
                {/* Prominent Warnings Section */}
                {getWarnings(item).length > 0 && (
                  <div className="p-4 rounded-lg border-2 border-amber-300 bg-amber-50 space-y-2">
                    <div className="flex items-center gap-2 text-amber-800 font-semibold">
                      <AlertTriangle className="h-5 w-5" />
                      {tx('Atención - Información importante:', 'Attention - Important information:')}
                    </div>
                    <ul className="space-y-1 ml-7">
                      {getWarnings(item).map((warning, idx) => (
                        <li key={idx} className={`text-sm ${warning.type === 'error' ? 'text-red-700 font-semibold' : 'text-amber-700'}`}>
                          {warning.text}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Price Type Selection */}
                <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
                  <Label className="text-base font-semibold mb-3 block">
                    {tx('Tipo de Temática', 'Topic Type')} <span className="text-red-500">*</span>
                  </Label>
                  <div className="flex gap-4">
                    <label className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer flex-1 transition-all ${
                      formData[item.id]?.price_type === 'normal' 
                        ? 'border-primary bg-primary/10' 
                        : 'border-muted hover:border-primary/50'
                    }`}>
                      <input
                        type="radio"
                        name={`price_type_${item.id}`}
                        value="normal"
                        checked={formData[item.id]?.price_type === 'normal'}
                        onChange={(e) => updateFormData(item.id, 'price_type', e.target.value)}
                        className="sr-only"
                      />
                      <div>
                        <p className="font-medium">{tx('Temática Normal', 'Normal Topic')}</p>
                        <p className="text-2xl font-bold text-primary">{item.price?.toFixed(2)} €</p>
                      </div>
                    </label>
                    {/* Solo mostrar opción sensible si el producto acepta contenido sensible */}
                    {(item.casino || item.gambling || item.cbd || item.cryptocurrency || item.sex || item.erotic || item.vpn || item.accepts_sensitive) && (
                      <label className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer flex-1 transition-all ${
                        formData[item.id]?.price_type === 'sensitive' 
                          ? 'border-primary bg-primary/10' 
                          : 'border-muted hover:border-primary/50'
                      }`}>
                        <input
                          type="radio"
                          name={`price_type_${item.id}`}
                          value="sensitive"
                          checked={formData[item.id]?.price_type === 'sensitive'}
                          onChange={(e) => updateFormData(item.id, 'price_type', e.target.value)}
                          className="sr-only"
                        />
                        <div>
                          <p className="font-medium">{tx('Temática Sensible', 'Sensitive Topic')}</p>
                          <p className="text-xs text-muted-foreground mb-1">(Casino, CBD, Gambling...)</p>
                          <p className="text-2xl font-bold text-primary">
                            {(item.sensitive_price || item.price_sensitive || item.price)?.toFixed(2)} €
                          </p>
                        </div>
                      </label>
                    )}
                  </div>
                </div>

                {/* Link Type - Dynamic options based on product */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    {tx('Tipo de enlace', 'Link Type')} 
                    <span className="text-red-500">*</span>
                    {item.link_type && !item.link_type.toUpperCase().includes('DOFOLLOW') && (
                      <span className="text-xs text-amber-600 font-normal">
                        ({tx('Limitado por el medio', 'Limited by publisher')})
                      </span>
                    )}
                  </Label>
                  <Select
                    value={formData[item.id]?.link_type}
                    onValueChange={(value) => updateFormData(item.id, 'link_type', value)}
                  >
                    <SelectTrigger className={!formData[item.id]?.link_type ? 'border-red-300' : ''}>
                      <SelectValue placeholder={tx('Selecciona una opción *', 'Select an option *')} />
                    </SelectTrigger>
                    <SelectContent>
                      {getAvailableLinkTypes(item).map((linkType) => (
                        <SelectItem key={linkType.value} value={linkType.value}>
                          {linkType.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {!formData[item.id]?.link_type && (
                    <p className="text-xs text-red-500">
                      {tx('Este campo es obligatorio', 'This field is required')}
                    </p>
                  )}
                </div>

                {/* Anchor and URL 1 */}
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Anchor 1 <span className="text-red-500">*</span></Label>
                    <Input
                      value={formData[item.id]?.anchor_1}
                      onChange={(e) => updateFormData(item.id, 'anchor_1', e.target.value)}
                      placeholder="Anchor 1"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>URL 1 <span className="text-red-500">*</span></Label>
                    <Input
                      value={formData[item.id]?.url_1}
                      onChange={(e) => updateFormData(item.id, 'url_1', e.target.value)}
                      placeholder="URL 1"
                    />
                  </div>
                </div>

                {/* Anchor and URL 2 (optional) */}
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Anchor 2 <span className="text-muted-foreground text-xs">({tx('opcional', 'optional')})</span></Label>
                    <Input
                      value={formData[item.id]?.anchor_2}
                      onChange={(e) => updateFormData(item.id, 'anchor_2', e.target.value)}
                      placeholder="Anchor 2"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>URL 2 <span className="text-muted-foreground text-xs">({tx('opcional', 'optional')})</span></Label>
                    <Input
                      value={formData[item.id]?.url_2}
                      onChange={(e) => updateFormData(item.id, 'url_2', e.target.value)}
                      placeholder="URL 2"
                    />
                  </div>
                </div>

                {/* Word Count and Publication Date */}
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>
                      {tx('Número de Palabras', 'Word Count')}
                      <span className="text-red-500 ml-1">*</span>
                    </Label>
                    <Select
                      value={formData[item.id]?.word_count}
                      onValueChange={(value) => updateFormData(item.id, 'word_count', value)}
                    >
                      <SelectTrigger className={!formData[item.id]?.word_count ? 'border-red-300' : ''}>
                        <SelectValue placeholder={tx('Selecciona una opción', 'Select an option')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="own_text">
                          {tx('✍️ Envío yo el Texto (Gratis)', '✍️ I will send the text (Free)')}
                        </SelectItem>
                        <SelectItem value="500">
                          500 {tx('palabras (Gratis)', 'words (Free)')}
                        </SelectItem>
                        <SelectItem value="600">
                          600 {tx('palabras (+6€)', 'words (+€6)')}
                        </SelectItem>
                        <SelectItem value="700">
                          700 {tx('palabras (+7€)', 'words (+€7)')}
                        </SelectItem>
                        <SelectItem value="800">
                          800 {tx('palabras (+8€)', 'words (+€8)')}
                        </SelectItem>
                        <SelectItem value="900">
                          900 {tx('palabras (+9€)', 'words (+€9)')}
                        </SelectItem>
                        <SelectItem value="1000">
                          1000 {tx('palabras (+10€)', 'words (+€10)')}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{tx('Fecha de publicación', 'Publication Date')}</Label>
                    <Input
                      type="date"
                      value={formData[item.id]?.publication_date}
                      onChange={(e) => updateFormData(item.id, 'publication_date', e.target.value)}
                      min={(() => {
                        const minDate = new Date();
                        minDate.setDate(minDate.getDate() + 5);
                        return minDate.toISOString().split('T')[0];
                      })()}
                    />
                    <p className="text-xs text-muted-foreground">
                      {tx(
                        'Mínimo 5 días desde hoy',
                        'Minimum 5 days from today'
                      )}
                    </p>
                  </div>
                </div>

                {/* Checkboxes with expandable fields */}
                <div className="space-y-4">
                  {/* Review before publish */}
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id={`review_${item.id}`}
                      checked={formData[item.id]?.review_before_publish}
                      onCheckedChange={(checked) => updateFormData(item.id, 'review_before_publish', checked)}
                    />
                    <Label htmlFor={`review_${item.id}`} className="cursor-pointer">
                      {tx(
                        '¿Quieres revisar el anuncio antes de publicarlo?',
                        'Do you want to review the ad before publishing?'
                      )}
                    </Label>
                  </div>

                  {/* Internal link checkbox with expandable fields */}
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={`internal_${item.id}`}
                        checked={formData[item.id]?.add_internal_link}
                        onCheckedChange={(checked) => updateFormData(item.id, 'add_internal_link', checked)}
                      />
                      <Label htmlFor={`internal_${item.id}`} className="cursor-pointer">
                        {tx(
                          '¿Agregar adicionalmente enlace interno?',
                          'Add additional internal link?'
                        )}
                      </Label>
                    </div>
                    {/* Expandable fields for internal link */}
                    {formData[item.id]?.add_internal_link && (
                      <div className="ml-6 p-4 bg-muted/30 rounded-lg border border-muted space-y-3">
                        <div className="grid gap-4 sm:grid-cols-2">
                          <div className="space-y-2">
                            <Label className="text-sm">
                              URL {tx('interno', 'internal')}
                            </Label>
                            <Input
                              value={formData[item.id]?.internal_link_url}
                              onChange={(e) => updateFormData(item.id, 'internal_link_url', e.target.value)}
                              placeholder="https://..."
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-sm">
                              Anchor text
                            </Label>
                            <Input
                              value={formData[item.id]?.internal_link_anchor}
                              onChange={(e) => updateFormData(item.id, 'internal_link_anchor', e.target.value)}
                              placeholder={tx('Texto del enlace', 'Link text')}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Authority site link checkbox with expandable fields */}
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={`authority_${item.id}`}
                        checked={formData[item.id]?.add_authority_link}
                        onCheckedChange={(checked) => updateFormData(item.id, 'add_authority_link', checked)}
                      />
                      <Label htmlFor={`authority_${item.id}`} className="cursor-pointer">
                        {tx(
                          '¿Agregar adicionalmente enlace a sitio de autoridad?',
                          'Add additional authority site link?'
                        )}
                      </Label>
                    </div>
                    {/* Expandable fields for authority link */}
                    {formData[item.id]?.add_authority_link && (
                      <div className="ml-6 p-4 bg-muted/30 rounded-lg border border-muted space-y-3">
                        <div className="grid gap-4 sm:grid-cols-2">
                          <div className="space-y-2">
                            <Label className="text-sm">
                              URL {tx('sitio de autoridad', 'authority site')}
                            </Label>
                            <Input
                              value={formData[item.id]?.authority_link_url}
                              onChange={(e) => updateFormData(item.id, 'authority_link_url', e.target.value)}
                              placeholder="https://..."
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-sm">
                              Anchor text
                            </Label>
                            <Input
                              value={formData[item.id]?.authority_link_anchor}
                              onChange={(e) => updateFormData(item.id, 'authority_link_anchor', e.target.value)}
                              placeholder={tx('Texto del enlace', 'Link text')}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <Label>{tx('Información para el redactor', 'Information for the writer')}</Label>
                  <Textarea
                    value={formData[item.id]?.description}
                    onChange={(e) => updateFormData(item.id, 'description', e.target.value)}
                    placeholder={tx(
                      'Introduzca información para el redactor...',
                      'Enter information for the writer...'
                    )}
                    rows={4}
                  />
                </div>

                {/* File Uploads */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      {tx('Cargar artículo', 'Upload article')}
                      <span className="text-muted-foreground text-xs">({tx('opcional', 'optional')})</span>
                    </Label>
                    <div className="border-2 border-dashed rounded-lg p-6 text-center hover:border-primary/50 transition-colors">
                      <input
                        type="file"
                        id={`article_${item.id}`}
                        className="hidden"
                        accept=".doc,.docx,.pdf,.txt"
                        onChange={(e) => handleFileChange(item.id, 'article_file', e.target.files[0])}
                      />
                      <label htmlFor={`article_${item.id}`} className="cursor-pointer">
                        <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">
                          {formData[item.id]?.article_file 
                            ? formData[item.id].article_file.name 
                            : (tx('Elige un Archivo', 'Choose a File'))}
                        </p>
                      </label>
                    </div>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        {tx('Cargar imagen 1', 'Upload image 1')}
                        <span className="text-muted-foreground text-xs">({tx('opcional', 'optional')})</span>
                      </Label>
                      <div className="border-2 border-dashed rounded-lg p-4 text-center hover:border-primary/50 transition-colors">
                        <input
                          type="file"
                          id={`image1_${item.id}`}
                          className="hidden"
                          accept="image/*"
                          onChange={(e) => handleFileChange(item.id, 'image_file_1', e.target.files[0])}
                        />
                        <label htmlFor={`image1_${item.id}`} className="cursor-pointer">
                          <Upload className="h-6 w-6 mx-auto mb-1 text-muted-foreground" />
                          <p className="text-xs text-muted-foreground">
                            {formData[item.id]?.image_file_1 
                              ? formData[item.id].image_file_1.name 
                              : (tx('Elige un Archivo', 'Choose a File'))}
                          </p>
                        </label>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        {tx('Cargar imagen 2', 'Upload image 2')}
                        <span className="text-muted-foreground text-xs">({tx('opcional', 'optional')})</span>
                      </Label>
                      <div className="border-2 border-dashed rounded-lg p-4 text-center hover:border-primary/50 transition-colors">
                        <input
                          type="file"
                          id={`image2_${item.id}`}
                          className="hidden"
                          accept="image/*"
                          onChange={(e) => handleFileChange(item.id, 'image_file_2', e.target.files[0])}
                        />
                        <label htmlFor={`image2_${item.id}`} className="cursor-pointer">
                          <Upload className="h-6 w-6 mx-auto mb-1 text-muted-foreground" />
                          <p className="text-xs text-muted-foreground">
                            {formData[item.id]?.image_file_2 
                              ? formData[item.id].image_file_2.name 
                              : (tx('Elige un Archivo', 'Choose a File'))}
                          </p>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Item Total */}
                <div className="flex justify-end pt-4 border-t">
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">{tx('Subtotal', 'Subtotal')}</p>
                    {/* Show price breakdown if there's a writing cost */}
                    {calculateWritingCost(formData[item.id]?.word_count) > 0 && (
                      <div className="text-xs text-muted-foreground mb-1">
                        <span>{tx('Producto: ', 'Product: ')}</span>
                        <span>
                          {(formData[item.id]?.price_type === 'sensitive' 
                            ? (item.sensitive_price || item.price_sensitive || item.price) 
                            : item.price
                          )?.toFixed(2)} €
                        </span>
                        <span className="mx-1">+</span>
                        <span className="text-purple-600">
                          {tx('Redacción: ', 'Writing: ')}
                          {calculateWritingCost(formData[item.id]?.word_count)}€
                        </span>
                      </div>
                    )}
                    <p className="text-2xl font-bold text-primary">{calculateItemPrice(item).toFixed(2)} €</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Order Summary and Submit */}
        <Card className="mt-8 sticky bottom-4">
          <CardContent className="p-6 space-y-4">
            {/* Wallet Balance Info */}
            <div className={`p-4 rounded-lg border ${walletBalance >= calculateTotal() ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Wallet className={`w-5 h-5 ${walletBalance >= calculateTotal() ? 'text-green-600' : 'text-red-600'}`} />
                  <span className="font-medium">
                    {tx('Tu saldo:', 'Your balance:')}
                  </span>
                  <span className={`font-bold ${walletBalance >= calculateTotal() ? 'text-green-700' : 'text-red-700'}`}>
                    {walletLoading ? '...' : `${walletBalance.toFixed(2)} €`}
                  </span>
                </div>
                {walletBalance < calculateTotal() && (
                  <Link to="/wallet">
                    <Button size="sm" variant="outline" className="border-red-300 text-red-700 hover:bg-red-100">
                      <CreditCard className="w-4 h-4 mr-1" />
                      {tx('Recargar', 'Top Up')}
                    </Button>
                  </Link>
                )}
              </div>
              {walletBalance < calculateTotal() && (
                <p className="text-sm text-red-600 mt-2">
                  {tx(
                    `Necesitas recargar ${(calculateTotal() - walletBalance).toFixed(2)} € más para completar el pedido.`,
                    `You need to top up ${(calculateTotal() - walletBalance).toFixed(2)} € more to complete the order.`
                  )}
                </p>
              )}
            </div>

            {/* Show applied coupon if any */}
            {appliedCoupon && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="bg-green-100 text-green-700">
                    {appliedCoupon.discount_type === 'percentage' 
                      ? `-${appliedCoupon.discount_value}%` 
                      : `-${appliedCoupon.discount_value}€`}
                  </Badge>
                  <span className="text-sm font-medium text-green-700">
                    {tx('Cupón aplicado:', 'Coupon applied:')} {appliedCoupon.code}
                  </span>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setAppliedCoupon(null)}
                  className="h-6 text-red-500 hover:text-red-700 hover:bg-red-50"
                >
                  {tx('Quitar', 'Remove')}
                </Button>
              </div>
            )}

            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{tx('Total del pedido', 'Order Total')}</p>
                <p className="text-3xl font-bold text-primary">{calculateTotal().toFixed(2)} €</p>
                {appliedCoupon && (
                  <p className="text-xs text-green-600">
                    {tx('(El descuento del cupón se aplicará al finalizar)', '(Coupon discount will be applied at checkout)')}
                  </p>
                )}
              </div>
              <div className="flex flex-col items-end gap-3">
                {/* Terms & Conditions checkbox — required to enable the submit button */}
                <label
                  className="flex items-start gap-2 cursor-pointer text-sm text-muted-foreground select-none max-w-md text-right"
                  data-testid="accept-terms-label"
                >
                  <Checkbox
                    checked={acceptedTerms}
                    onCheckedChange={(v) => setAcceptedTerms(!!v)}
                    data-testid="accept-terms-checkbox"
                    className="mt-0.5"
                  />
                  <span>
                    {tx('He leído y acepto los ', 'I have read and accept the ')}
                    <a
                      href="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary underline hover:no-underline"
                    >
                      {tx('Términos y Condiciones', 'Terms & Conditions')}
                    </a>
                    {tx(' y la ', ' and ')}
                    <a
                      href="/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary underline hover:no-underline"
                    >
                      {tx('Política de Privacidad', 'Privacy Policy')}
                    </a>
                    .
                  </span>
                </label>
                <Button
                  size="lg"
                  onClick={handleSubmit}
                  disabled={loading || walletBalance < calculateTotal() || !acceptedTerms}
                  className="px-8"
                  data-testid="checkout-submit-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {tx('Procesando...', 'Processing...')}
                    </>
                  ) : (
                    tx('Finalizar Pedido', 'Complete Order')
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Modal de saldo insuficiente: el usuario decide cuánto recargar (mín 10€) */}
      <Dialog open={!!insufficientModal} onOpenChange={(open) => { if (!open && !payingPaypal) { setInsufficientModal(null); setCustomAmount(''); } }}>
        <DialogContent className="max-w-md" data-testid="insufficient-balance-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5" />
              {tx('Saldo insuficiente', 'Insufficient balance')}
            </DialogTitle>
            <DialogDescription>
              {tx(
                'No tienes saldo suficiente para completar este pedido. Tu pedido NO se ha perdido — recarga tu monedero con PayPal y, si cubres el total, lo finalizaremos automáticamente.',
                "You don't have enough balance to complete this order. Your order is NOT lost — top up your wallet with PayPal and, if it covers the total, we'll finish it automatically."
              )}
            </DialogDescription>
          </DialogHeader>
          {insufficientModal && (
            <div className="space-y-3 py-2">
              <div className="flex justify-between text-sm">
                <span>{tx('Tu saldo actual', 'Current balance')}:</span>
                <span className="font-semibold">{(insufficientModal.current || 0).toFixed(2)} €</span>
              </div>
              {insufficientModal.total !== null && (
                <div className="flex justify-between text-sm">
                  <span>{tx('Total del pedido', 'Order total')}:</span>
                  <span className="font-semibold">{(insufficientModal.total || 0).toFixed(2)} €</span>
                </div>
              )}
              <div className="flex justify-between text-sm border-t pt-2 text-red-700">
                <span>{tx('Te falta como mínimo', 'You need at least')}:</span>
                <span className="font-semibold" data-testid="shortfall-amount">{insufficientModal.shortfall.toFixed(2)} €</span>
              </div>

              <div className="space-y-2 pt-2">
                <Label htmlFor="recharge-amount-input" className="text-sm font-medium">
                  {tx('¿Cuánto quieres recargar?', 'How much do you want to top up?')} <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <Input
                    id="recharge-amount-input"
                    type="number"
                    min={10}
                    step="0.01"
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    disabled={payingPaypal}
                    className="pr-8"
                    data-testid="recharge-amount-input"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">€</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {tx(
                    'Mínimo 10€. Puedes recargar más para futuras compras — el saldo no caduca.',
                    'Minimum €10. You can top up more for future purchases — the balance never expires.'
                  )}
                </p>
              </div>
            </div>
          )}
          <DialogFooter className="sm:justify-between gap-2">
            <Button variant="outline" disabled={payingPaypal} onClick={() => { setInsufficientModal(null); setCustomAmount(''); }} data-testid="insufficient-cancel-btn">
              {tx('Cancelar', 'Cancel')}
            </Button>
            <Button
              onClick={handlePayShortfall}
              disabled={payingPaypal || !customAmount || parseFloat(String(customAmount).replace(',', '.')) < 10}
              className="bg-[#0070BA] hover:bg-[#005a9c] text-white"
              data-testid="pay-with-paypal-btn"
            >
              {payingPaypal ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {tx('Procesando…', 'Processing…')}</>
              ) : (
                <>{tx('Recargar con PayPal', 'Top up with PayPal')}</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CheckoutPage;
